/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { useAvroTracking } from '../../../observability';
import { CustomBrandButton } from '../../CustomBrandButton/CustomBrandButton';

export type SimpleCTAProps = {
  heading: string;
  buttons?: {
    text?: string;
    url?: string;
    variant?: 'primary' | 'primary-alt' | 'secondary';
  }[];
  disableSpacing?: boolean;
};

const Styles = labelStyles('SimpleCTA', {
  root: css({
    padding: '0 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    '&:not([data-disable-spacing="true"])': {
      padding: '48px 20px',

      [MEDIA_MIN_WIDTH.small]: {
        padding: '64px 20px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: '96px 20px',
      },
    },
  }),

  heading: css({
    fontFamily: Typography.heading.fontFamily,
    fontSize: '40px',
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '1px',
    textAlign: 'center',
    color: Colors.earth,
    maxWidth: '600px',

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '70px',
      letterSpacing: '1.5px',
    },
  }),

  buttons: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '24px',

    [MEDIA_MIN_WIDTH.small]: {
      flexDirection: 'row',
      marginTop: '32px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      marginTop: '48px',
    },
  }),
});

export function SimpleCTA(props: SimpleCTAProps) {
  const { emitCTAClickEvent } = useAvroTracking();

  return (
    <div css={Styles.root} data-disable-spacing={props.disableSpacing}>
      <h2 css={Styles.heading}>{props.heading}</h2>
      {props.buttons && props.buttons.length > 0 && (
        <div css={Styles.buttons}>
          {props.buttons.map((btn, index) => (
            <CustomBrandButton
              key={index}
              variant={btn.variant}
              href={btn.url}
              onClick={() => {
                emitCTAClickEvent({
                  text: btn.text ?? '',
                  href: btn.url ?? '',
                  section: 'SimpleCTA',
                });
              }}
            >
              {btn.text}
            </CustomBrandButton>
          ))}
        </div>
      )}
    </div>
  );
}
